import React, { useEffect, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { dipendenteService, orariGeneratiService } from '../../api/services';
import { TurnoCreato } from '../../types/types';
import { esportaScreenshotMatrice, OrarioMatriceLike } from './GenerazioneOrarioPDF';

dayjs.locale('it');

interface HiddenScheduleRendererProps {
  negozio: string;
  mese: number;
  anno: number;
  onComplete: () => void;
  onError: (msg: string) => void;
}

const HiddenScheduleRenderer: React.FC<HiddenScheduleRendererProps> = ({
  negozio,
  mese,
  anno,
  onComplete,
  onError
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [matrice, setMatrice] = useState<OrarioMatriceLike[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const start = dayjs(`${anno}-${mese}-01`).format('YYYY-MM-DD');
        const end = dayjs(`${anno}-${mese}-01`).endOf('month').format('YYYY-MM-DD');

        const [orari, dips] = await Promise.all([
          orariGeneratiService.getByNegozioPeriodo(negozio, start, end),
          dipendenteService.getByNegozio(negozio)
        ]);

        // Costruisci la matrice
        const map = new Map<string, OrarioMatriceLike>();
        
        // Inizializza per tutti i dipendenti
        dips.forEach(d => {
          map.set(d.nome, { dipendente: d.nome, orari: {} });
        });

        // Popola con gli orari (filtra solo i turni del negozio corrente)
        orari.forEach(o => {
          // Filtra solo i turni che appartengono al negozio per cui stiamo scaricando
          if (o.neg !== negozio) {
            return;
          }

          // Trova il nome del dipendente se non c'è in o.descrizione
          let nome = o.descrizione;
          if (!nome) {
            const d = dips.find(x => x.codicePersonale === o.codicePersonale);
            nome = d ? d.nome : (o.codicePersonale || 'Sconosciuto');
          }

          if (!map.has(nome)) {
            map.set(nome, { dipendente: nome, orari: {} });
          }

          const entry = map.get(nome)!;
          const day = dayjs(o.data).date();
          
          if (!entry.orari[day]) {
            entry.orari[day] = [];
          }
          entry.orari[day].push(o);
        });

        const sortedMatrice = Array.from(map.values()).sort((a, b) => a.dipendente.localeCompare(b.dipendente));
        setMatrice(sortedMatrice);
        setLoading(false);
      } catch (e: any) {
        console.error(e);
        // Distingui tra errore 403 (accesso negato) e altri errori
        if (e?.response?.status === 403) {
          onError('Accesso negato al negozio. Verifica i permessi del tuo utente.');
        } else {
          onError('Errore nel recupero degli orari');
        }
      }
    };

    fetchData();
  }, [negozio, mese, anno, onError]);

  useEffect(() => {
    if (!loading && matrice.length > 0 && containerRef.current) {
      // Attendi rendering
      setTimeout(async () => {
        try {
          const mesi = [
            'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
            'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
          ];
          
          await esportaScreenshotMatrice({
            container: containerRef.current,
            negozio,
            meseIndex: mese - 1,
            anno,
            mesi,
            setScreenshotMode: () => {}, // Non serve qui
            delayMs: 500
          });
          onComplete();
        } catch (e) {
          console.error(e);
          onError('Errore durante la generazione dello screenshot');
        }
      }, 1000);
    } else if (!loading && matrice.length === 0) {
        onError('Nessun orario trovato per il periodo selezionato');
    }
  }, [loading, matrice, negozio, mese, anno, onComplete, onError]);

  if (loading) return null;

  const daysInMonth = dayjs(`${anno}-${mese}-01`).daysInMonth();
  const startOfMonth = dayjs(`${anno}-${mese}-01`);
  const startDayOfWeek = startOfMonth.day(); // 0=Sun, 1=Mon, ..., 6=Sat

  // Calcola il giorno di split
  // Se inizia Ven(5), Sab(6) o Dom(0) -> 3a domenica
  // Altrimenti -> 2a domenica
  const isException = startDayOfWeek === 5 || startDayOfWeek === 6 || startDayOfWeek === 0;
  const targetSundayCount = isException ? 3 : 2;

  let splitDay = daysInMonth; // Default: tutto in una tabella se non troviamo abbastanza domeniche
  let sundayCount = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const date = dayjs(`${anno}-${mese}-${d}`);
    if (date.day() === 0) {
      sundayCount++;
      if (sundayCount === targetSundayCount) {
        splitDay = d;
        break;
      }
    }
  }

  const daysPart1 = Array.from({ length: splitDay }, (_, i) => i + 1);
  const daysPart2 = Array.from({ length: daysInMonth - splitDay }, (_, i) => splitDay + i + 1);

  const renderTable = (days: number[]) => (
    <Table size="small" sx={{ borderCollapse: 'collapse', mb: 4 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ border: '1px solid #ccc', fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Dipendente</TableCell>
            {days.map(d => {
                const date = dayjs(`${anno}-${mese}-${d}`);
                const isWeekend = date.day() === 0 || date.day() === 6;
                return (
                    <TableCell 
                        key={d} 
                        align="center" 
                        sx={{ 
                            border: '1px solid #ccc', 
                            fontWeight: 'bold', 
                            bgcolor: isWeekend ? '#eeeeee' : '#f5f5f5',
                            minWidth: 40
                        }}
                    >
                        <Box>{d}</Box>
                        <Box sx={{ fontSize: '0.7rem' }}>{date.format('ddd')}</Box>
                    </TableCell>
                );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {matrice.map((row) => (
            <TableRow key={row.dipendente}>
              <TableCell sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}>{row.dipendente}</TableCell>
              {days.map(d => {
                const orariGiorno = row.orari[d] || [];
                const date = dayjs(`${anno}-${mese}-${d}`);
                const isWeekend = date.day() === 0 || date.day() === 6;
                
                return (
                  <TableCell 
                    key={d} 
                    align="center" 
                    sx={{ 
                        border: '1px solid #ccc',
                        bgcolor: isWeekend ? '#fafafa' : 'white',
                        p: 0.5,
                        verticalAlign: 'top'
                    }}
                  >
                    {orariGiorno.map((o: TurnoCreato, idx: number) => {
                      const turno = (o.turno || '').toLowerCase();
                      const codice = (o.codiceTipoOrario || '').toLowerCase();
                      
                      let bgcolor = '#ffffff';
                      let borderColor = '#e0e0e0';
                      let content: React.ReactNode = null;
                      
                      if (codice === 'assenza' || turno === 'assenza') {
                          bgcolor = '#ffcdd2';
                          borderColor = '#ef5350';
                          content = 'Assenza';
                      } else if (codice === 'riposo' || turno === 'riposo') {
                          bgcolor = '#eeeeee';
                          borderColor = '#bdbdbd';
                          content = 'Riposo';
                      } else if (turno.includes('mattina')) {
                          bgcolor = '#e3f2fd';
                          borderColor = '#90caf9';
                          if (o.oraPrevInizio && o.oraPrevFine) {
                              content = (
                                <>
                                  <div>{o.oraPrevInizio.substring(0, 5)}</div>
                                  <div>{o.oraPrevFine.substring(0, 5)}</div>
                                </>
                              );
                          }
                      } else if (turno.includes('pomeriggio')) {
                          bgcolor = '#fff9c4';
                          borderColor = '#fff59d';
                          if (o.oraPrevInizio && o.oraPrevFine) {
                              content = (
                                <>
                                  <div>{o.oraPrevInizio.substring(0, 5)}</div>
                                  <div>{o.oraPrevFine.substring(0, 5)}</div>
                                </>
                              );
                          }
                      } else if (o.oraPrevInizio && o.oraPrevFine) {
                          // Fallback per altri turni (es. Intermezzo)
                          bgcolor = '#f1f8e9';
                          borderColor = '#a5d6a7';
                          content = (
                            <>
                              <div>{o.oraPrevInizio.substring(0, 5)}</div>
                              <div>{o.oraPrevFine.substring(0, 5)}</div>
                            </>
                          );
                      }

                      if (!content) return null;

                      return (
                        <Box key={idx} sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                            {content && (
                                <Box sx={{ 
                                    bgcolor, 
                                    borderRadius: 1, 
                                    p: 0.25,
                                    border: `1px solid ${borderColor}`,
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>
                                    {content}
                                </Box>
                            )}
                        </Box>
                      );
                    })}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
  );

  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        position: 'absolute', 
        top: -9999, 
        left: -9999, 
        width: 'fit-content', // Importante per html2canvas
        bgcolor: 'white',
        p: 2
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">
            Calendario Orari - {dayjs().month(mese - 1).format('MMMM')} {anno} - {negozio}
        </Typography>
      </Box>
      
      {renderTable(daysPart1)}
      
      {daysPart2.length > 0 && (
        <>
            <Box sx={{ height: 20 }} /> {/* Spaziatore */}
            {renderTable(daysPart2)}
        </>
      )}
    </Box>
  );
};

export default HiddenScheduleRenderer;
